import React, { useState } from 'react';
import { Eye, EyeOff, Mail, Lock, User, AlertCircle, Shield, Zap, Info, RotateCcw } from 'lucide-react';
import { ThemeToggle } from '../Layout/ThemeToggle';
import { AppLogo } from '../Layout/AppLogo';
import { clearAllAuthData } from '../../lib/supabase';

interface AuthFormProps {
  onLogin: (email: string, password: string) => Promise<{ success: boolean; error?: string; message?: string }>;
  onSignup: (name: string, email: string, password: string) => Promise<{ success: boolean; error?: string; message?: string }>;
  onResetPassword: (email: string) => Promise<{ success: boolean; error?: string; message?: string }>;
}

export const AuthForm: React.FC<AuthFormProps> = ({ onLogin, onSignup, onResetPassword }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState<NodeJS.Timeout | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    
    setIsLoading(true);
    setError('');
    setMessage('');


    try {
      let result;
      if (showForgotPassword) {
        result = await onResetPassword(formData.email.trim());
      } else {
        result = isLogin
          ? await onLogin(formData.email.trim(), formData.password)
          : await onSignup(formData.name.trim(), formData.email.trim(), formData.password);
      }


      if (!result.success) {
        setError(result.error || 'Authentication failed');
      } else {
        if (result.message) {
          setMessage(result.message);
        }
      }
    } catch (err) {
      console.error('AuthForm: Unexpected error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }
    };
  }, [loadingTimeout]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) setError('');
    if (message) setMessage('');
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError('');
    setMessage('');
    setShowForgotPassword(false);
    setFormData({ name: '', email: '', password: '' });
  };

  const handleForgotPassword = () => {
    setShowForgotPassword(true);
    setError('');
    setMessage('');
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setError('');
    setMessage('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-modular-shadow-quartz via-modular-graphite-smoke to-modular-frosted-indigo 
      dark:bg-dark-gradient flex items-center justify-center p-4 transition-all duration-500 relative overflow-hidden">
      
      {/* Subtle animated particles background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-1 h-1 bg-modular-accent-blue/40 dark:bg-modular-accent-blue/20 rounded-full animate-ember-float opacity-40 dark:opacity-20" />
        <div className="absolute top-40 right-20 w-0.5 h-0.5 bg-modular-accent-emerald/40 dark:bg-modular-accent-emerald/20 rounded-full animate-ember-float opacity-30 dark:opacity-15" 
          style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-32 left-1/4 w-1 h-1 bg-modular-accent-blue/30 dark:bg-modular-accent-blue/15 rounded-full animate-ember-float opacity-35 dark:opacity-18" 
          style={{ animationDelay: '2s' }} />
        <div className="absolute bottom-20 right-1/3 w-0.5 h-0.5 bg-modular-accent-emerald/30 dark:bg-modular-accent-emerald/15 rounded-full animate-ember-float opacity-25 dark:opacity-12" 
          style={{ animationDelay: '0.5s' }} />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="bg-white/95 dark:bg-dark-surface/95 backdrop-blur-xl rounded-2xl 
          border border-modular-stone-alloy/30 dark:border-dark-border/50 p-8 shadow-lg 
          dark:shadow-dark-elevated transition-all duration-300">
          
          <div className="flex justify-end items-center mb-4">
            <ThemeToggle />
          </div>

          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-6">
              <AppLogo size="lg" animated={false} />
              <div className="ml-4">
                <h1 className="text-4xl font-bold text-gray-900 dark:text-dark-text">
                  PromptOS
                </h1>
                <p className="text-lg text-modular-desert-mauve dark:text-modular-stone-alloy font-medium -mt-1">
                  AI Prompt Operating System
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text">
                {showForgotPassword 
                  ? 'Reset Your Password'
                  : isLogin
                    ? 'Welcome Back to PromptOS'
                    : 'Create Account'}
              </h2>
              <p className="text-gray-600 dark:text-dark-text-secondary text-sm">
                {showForgotPassword
                  ? 'Enter your email to receive a password reset link'
                  : isLogin 
                    ? 'Access your private collection of intelligent prompts' 
                    : 'Join your secure vault of creative power'}
              </p>
            </div>

            {/* Security badges */}
            <div className="flex items-center justify-center space-x-4 mt-4">
              <div className="flex items-center text-xs text-modular-accent-emerald dark:text-modular-accent-emerald 
                bg-emerald-50 dark:bg-emerald-900/20 px-3 py-1 rounded-full border border-emerald-200 dark:border-emerald-800/30"> {/* Keep emerald */}
                <Shield className="h-3 w-3 mr-1" />
                Encrypted
              </div>
              <div className="flex items-center text-xs text-modular-accent-blue dark:text-modular-accent-blue 
                bg-blue-50 dark:bg-blue-900/20 px-3 py-1 rounded-full border border-blue-200 dark:border-blue-800/30">
                <Zap className="h-3 w-3 mr-1" />
                Private
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && !showForgotPassword && (
              <div className="relative group">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 
                  text-gray-400 dark:text-gray-500 group-focus-within:text-modular-accent-blue dark:group-focus-within:text-modular-accent-blue transition-colors" />
                <input
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-dark-card border 
                    border-gray-200 dark:border-dark-border rounded-lg text-gray-900 dark:text-dark-text 
                    placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 
                    focus:ring-modular-accent-blue dark:focus:ring-modular-accent-blue 
                    focus:border-transparent transition-all duration-300"
                  required={!isLogin}
                  minLength={2}
                  autoComplete="name"
                />
              </div>
            )}

            <div className="relative group">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 
                text-gray-400 dark:text-gray-500 group-focus-within:text-modular-accent-blue dark:group-focus-within:text-modular-accent-blue transition-colors" />
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-dark-card border 
                  border-gray-200 dark:border-dark-border rounded-lg text-gray-900 dark:text-dark-text 
                  placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 
                  focus:ring-modular-accent-blue dark:focus:ring-modular-accent-blue 
                  focus:border-transparent transition-all duration-300"
                required
                autoComplete="email"
              />
            </div>

            {!showForgotPassword && (
              <div className="relative group">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 
                  text-gray-400 dark:text-gray-500 group-focus-within:text-modular-accent-blue dark:group-focus-within:text-modular-accent-blue transition-colors" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-12 py-3 bg-gray-50 dark:bg-dark-card border 
                    border-gray-200 dark:border-dark-border rounded-lg text-gray-900 dark:text-dark-text 
                    placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 
                    focus:ring-modular-accent-blue dark:focus:ring-modular-accent-blue 
                    focus:border-transparent transition-all duration-300"
                  required
                  minLength={isLogin ? 1 : 6}
                  autoComplete={isLogin ? "current-password" : "new-password"}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 
                    hover:text-modular-accent-blue dark:hover:text-modular-accent-blue 
                    transition-colors duration-300"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            )}
            {!isLogin && !showForgotPassword && (
              <div className="text-xs text-gray-600 dark:text-dark-text-secondary bg-modular-canvas-fog dark:bg-modular-shadow-quartz/20 
                  rounded-lg p-3 border border-modular-stone-alloy/30 dark:border-modular-stone-alloy/30">
                <p className="mb-2 font-medium text-modular-accent-blue dark:text-modular-accent-blue">Account Creation:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Strong password (6+ characters)</li>
                  <li>Email confirmation required</li>
                  <li>Your data will be encrypted and private</li>
                </ul>
              </div>
            )}

            {error && (
              <div className="flex items-start text-red-600 dark:text-red-400 text-sm
                bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 
                rounded-lg p-3 transition-colors duration-300">
                <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                <div className="leading-relaxed">
                  <p>{error}</p>
                  {(error.includes('Invalid') || error.includes('invalid')) && (
                    <div className="mt-2 text-xs">
                      <p className="font-medium">Common solutions:</p>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>If you just signed up, check email for confirmation link first</li>
                        <li>Verify you're using the correct password</li>
                        <li>Try resetting your password if needed</li>
                      </ul>
                    </div>
                  )}
                  {error.includes('email') && error.includes('already') && (
                    <div className="mt-2 text-xs">
                      <p className="font-medium">Account already exists:</p>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>Try signing in instead</li>
                        <li>Use "Forgot password" if you can't remember your password</li>
                        <li>Check your email for any pending confirmation links</li>
                      </ul>
                    </div>
                  )}
                  {error.includes('URL') && (
                    <div className="mt-2 text-xs">
                      <p className="font-medium">Configuration issue:</p>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>This is a setup issue with the authentication system</li>
                        <li>Please try again in a few minutes</li>
                        <li>Contact support if the issue persists</li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {message && (
              <div className="flex items-start text-emerald-600 dark:text-emerald-400 text-sm
                bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/30 
                rounded-lg p-3 transition-colors duration-300">
                <Info className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                <div className="leading-relaxed">
                  <p>{message}</p>
                  {message.includes('confirmation') && (
                    <p className="mt-2 text-xs text-emerald-500 dark:text-emerald-400">
                      <strong>Important:</strong> Check spam folder if you don't see the email within a few minutes.
                    </p>
                  )}
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-modular-primary-gradient text-white font-semibold 
                py-3 px-6 rounded-lg focus:outline-none focus:ring-2 focus:ring-modular-accent-blue 
                focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-dark-surface 
                transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed 
                shadow-modular-card hover:shadow-modular-glow transform hover:scale-[1.02] 
                active:scale-[0.98] relative overflow-hidden group"
            >
              <div className="absolute inset-0 bg-modular-secondary-gradient 
                opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative z-10 flex items-center justify-center">
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {showForgotPassword 
                      ? 'Sending Reset Link...' 
                      : isLogin 
                        ? 'Signing In...' 
                        : 'Creating Account...'}
                  </>
                ) : (
                  <>
                    <AppLogo size="sm" animated={false} />
                    <span className="ml-2">
                      {showForgotPassword 
                        ? 'Send Reset Link' 
                        : isLogin 
                          ? 'Sign In' 
                          : 'Create Account'}
                    </span>
                  </>
                )}
              </div>
            </button>
          </form>

          <div className="mt-6 text-center space-y-3">
            {showForgotPassword ? (
              <button
                onClick={handleBackToLogin}
                className="flex items-center justify-center bg-modular-primary-gradient hover:shadow-modular-glow text-white px-4 py-2 rounded-lg
                  font-semibold transition-colors duration-300 hover:underline mx-auto"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Back to Login
              </button>
            ) : (
              <>
                <p className="text-gray-600 dark:text-dark-text-secondary">
                  {isLogin ? "Don't have an account?" : 'Already have an account?'}
                  <button
                    onClick={toggleMode}
                    className="ml-2 text-modular-accent-blue dark:text-modular-accent-blue hover:text-blue-700 
                      dark:hover:text-blue-300 font-semibold transition-colors duration-300 
                      hover:underline"
                  >
                    {isLogin ? 'Create one' : 'Sign in'}
                  </button>
                </p>
                
                {isLogin && (
                  <button
                    onClick={handleForgotPassword}
                    className="text-sm text-gray-500 dark:text-gray-400 hover:text-modular-accent-blue
                      dark:hover:text-modular-accent-blue transition-colors duration-300 hover:underline"
                  >
                    Forgot your password?
                  </button>
                )}
              </>
            )}
          </div>

          {isLogin && !showForgotPassword && (
            <div className="mt-6 text-center">
              <div className="flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 
                bg-gray-50 dark:bg-dark-card rounded-lg p-3 border border-gray-200 dark:border-dark-border">
                <Shield className="h-4 w-4 mr-2 text-modular-accent-emerald dark:text-modular-accent-emerald" />
                <span>Your prompts are encrypted and stored securely in PromptOS</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};